import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { IftaLabel } from 'primeng/iftalabel';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Typing this key commits the typed text as a label, like Enter. */
const LABEL_SEPARATOR = ',';

const sameLabel = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

/**
 * The one labels field of the app (export dialogs, File Center, shareable "Labels
 * bearbeiten") — change label-entry behavior here, not in a local `p-autocomplete`.
 *
 * Suggests `labelOptions` that are not selected yet, plus a "Neues Label" entry for typed
 * text that matches no option. `p-autocomplete` with `multiple` + `typeahead` only adds a
 * value when a suggestion is picked — Enter, Tab and blur drop free text (PrimeNG's
 * `addOnBlur` / `separator` are disabled in typeahead mode). So the typed text is
 * committed here: on Enter (unless PrimeNG already picked a highlighted suggestion), on
 * `,` and on blur (BDA-4998). Labels are compared case-insensitively.
 */
@Component({
  selector: 'bd-ui-labels-input',
  template: `
    <p-iftalabel>
      <p-autocomplete
        [formControl]="inputControl"
        [suggestions]="suggestions()"
        [typeahead]="true"
        [completeOnFocus]="hasOptions()"
        [showEmptyMessage]="false"
        multiple
        fluid
        variant="filled"
        [inputId]="inputId()"
        (completeMethod)="onCompleteMethod($event)"
        (keydown)="onKeydown($event)"
        (onBlur)="onInputBlur($event)"
        (onFocus)="onInputFocus()"
        (onSelect)="onSelect($event)"
      >
        <ng-template let-label #item>
          <div class="inline-flex items-center gap-2">
            @if (isNewLabel(label)) {
              <i class="pi pi-plus text-xs"></i>
              <span>Neues Label: „{{ label }}“</span>
            } @else {
              <i class="pi pi-tag text-xs"></i>
              <span>{{ label }}</span>
            }
          </div>
        </ng-template>
      </p-autocomplete>
      <label [for]="inputId()">Labels</label>
    </p-iftalabel>
  `,
  imports: [ReactiveFormsModule, AutoComplete, IftaLabel],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdUiLabelsInputComponent {
  readonly labels = input<string[] | null>([]);
  /** Existing labels offered as suggestions (e.g. every label the user already uses). */
  readonly labelOptions = input<string[] | null | undefined>();
  readonly inputId = input('labels-input');

  readonly labelsChange = output<string[]>();

  readonly inputControl = new FormControl<string[]>([]);
  readonly suggestions = signal<string[]>([]);
  readonly hasOptions = computed(() => (this.labelOptions() ?? []).length > 0);

  /**
   * Label the last blur committed. Clicking a suggestion blurs the input before the click
   * selects it, so a partly typed query ("Sch") would land next to the picked "Schlaf".
   */
  private _committedOnBlur: string | null = null;

  constructor() {
    this.inputControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((labels) => this.labelsChange.emit(labels));

    effect(() => {
      const labels = this.labels();
      if (labels) {
        this.inputControl.patchValue(labels, { emitEvent: false });
      }
    });

    effect(() => {
      this.labelOptions();
      this.suggestions.set(this._unselectedOptions());
    });
  }

  public onCompleteMethod(event: AutoCompleteCompleteEvent): void {
    const query = event.query.trim();
    const pool = this._unselectedOptions();
    if (query.length === 0) {
      this.suggestions.set(pool);
      return;
    }
    const lower = query.toLowerCase();
    const matches = pool.filter((option) =>
      option.toLowerCase().includes(lower),
    );
    const known =
      matches.some((option) => sameLabel(option, query)) ||
      this._isSelected(query);
    this.suggestions.set(known ? matches : [query, ...matches]);
  }

  /** Whether `label` would be created rather than reused from `labelOptions`. */
  public isNewLabel(label: string): boolean {
    return !(this.labelOptions() ?? []).some((option) =>
      sameLabel(option, label),
    );
  }

  /**
   * Bubbles up from the input after PrimeNG's own handler. A highlighted suggestion picked
   * by Enter has already emptied the input, so only unpicked text is committed here.
   * `preventDefault` keeps the same Enter from also confirming the surrounding dialog.
   */
  public onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== LABEL_SEPARATOR) {
      return;
    }
    if (event.isComposing || !(event.target instanceof HTMLInputElement)) {
      return;
    }
    // A separator is never part of a label; Enter is only claimed when there was text.
    if (event.key === LABEL_SEPARATOR || event.target.value.trim().length > 0) {
      event.preventDefault();
    }
    this._commitTypedText(event.target);
  }

  public onInputBlur(event: Event): void {
    this._committedOnBlur = this._commitTypedText(event.target);
  }

  public onInputFocus(): void {
    this._committedOnBlur = null;
  }

  /** Drops the partial query the preceding blur committed when another suggestion was picked. */
  public onSelect(event: AutoCompleteSelectEvent): void {
    const partial = this._committedOnBlur;
    this._committedOnBlur = null;
    if (partial === null || partial === event.value) {
      return;
    }
    this.inputControl.setValue(
      (this.inputControl.value ?? []).filter((label) => label !== partial),
    );
  }

  /**
   * Adds the input's text as a label and empties the input. Returns the added label, or
   * `null` when there was nothing new to add (empty text or an existing label).
   */
  private _commitTypedText(target: EventTarget | null): string | null {
    if (!(target instanceof HTMLInputElement)) {
      return null;
    }
    const text = target.value.trim();
    if (text.length === 0) {
      return null;
    }
    target.value = '';
    if (this._isSelected(text)) {
      return null;
    }
    this.inputControl.setValue([...(this.inputControl.value ?? []), text]);
    return text;
  }

  private _isSelected(text: string): boolean {
    return (this.inputControl.value ?? []).some((label) =>
      sameLabel(label, text),
    );
  }

  private _unselectedOptions(): string[] {
    return (this.labelOptions() ?? []).filter(
      (option) => !this._isSelected(option),
    );
  }
}
